<template>
    <b-modal
        id="internship-modal"
        v-model="modal"
        v-if="internship"
        modal-class="apprentice-default-modal internship-detail-modal"
        ref="internship-modal"
        size="lg"
        :title="internship.name"
        :ok-title="$t('common.save')"
        @ok="onOk"
        ok-only>
        <template #modal-header="{ close }">
            <div class="header">
                <img
                    :src="internship.file ? internship.file.public_path : '/images/no-media.jpg'"
                    :alt="`Banner ${internship.name}`" />
                <b-button
                    class="btn-times btn-close"
                    variant="primary-icon-only"
                    @click="close()">
                </b-button>
            </div>
        </template>

        <div class="internship-details">
            <div class="internship-details-header">
                <div class="internship-title">
                    <h3 class="internship-text">{{ internship.name }}</h3>
                </div>
            </div>

            <div class="additional-info">
                <b-form-group
                    class="internship-tags"
                    v-if="internshipTagsIn.length"
                    label-for="internship-tags-input"
                    label="Labels">
                    <design-2021-apprentice-multiselect-component
                        v-model="tags"
                        :allow-empty="true"
                        label="name"
                        :placeholder="$t('internship.choose_label')"
                        :options="internshipTagsIn"
                        track-by="id">
                    </design-2021-apprentice-multiselect-component>
                </b-form-group>

                <div class="internship-organisation">
                    <span class="label">{{ $t('common.organization') }}</span>
                    <span class="organization">{{ internship.organization }}</span>
                </div>

                <div
                    class="internship-location"
                    v-if="internship.location">
                    <span class="label">{{ $t('common.location') }}</span>

                    <span class="location">
                        <i class="fas fa-map-marker-alt gg-map-icon"></i>
                        {{ internship.location }}
                    </span>
                    <span
                        class="country"
                        v-if="internship.country">
                        {{ internship.country }}
                    </span>
                </div>

                <div
                    class="internship-email"
                    v-if="internship.email">
                    <span class="label">{{ $t('common.email_address') }}</span>
                    <a :href="`mailto:${internship.email}`">{{ internship.email }}</a>
                </div>

                <div
                    class="internship-phone-number"
                    v-if="internship.phone">
                    <span class="label">{{ $t('common.phone_number') }}</span>
                    <a :href="`tel:${internship.phone}`">{{ internship.phone }}</a>
                </div>
            </div>

            <div class="internship-details-description">
                <h4 class="internship-details-description-title">{{ $t('common.description') }}</h4>
                <design-2021-apprentice-display-html-component v-html="internship.description">
                </design-2021-apprentice-display-html-component>
            </div>

            <div class="internship-details-map-location">
                <design-2021-apprentice-map-component
                    class="internship-detail-google-map"
                    :title="internship.organization"
                    :address="getInternshipAddress()"
                    :google-map-api="googleMapApi">
                </design-2021-apprentice-map-component>
            </div>
        </div>
    </b-modal>
</template>

<script>
export default {
    name: 'design-2021-internship-modal-component',
    emits: ['update'],
    props: ['internshipTagsIn', 'googleMapApi'],
    data() {
        return {
            internship: null,
            modal: false,
            tags: [],
        };
    },
    methods: {
        show(internship) {
            this.modal = true;
            this.internship = internship;
            this.tags = internship.internship_tags;
        },

        onOk() {
            this.alert.saving();
            const tagIds = this.tags.map((tag) => tag.id);
            let data = {
                tagIds: tagIds,
            };
            this.axios
                .post(this.routes.internshipUpdateTags(this.internship.id), data)
                .then((response) => {
                    this.internship.update(response.data);
                    this.alert.success();
                })
                .catch(function (error) {
                    this.alert.ajaxError(error);
                });
        },

        getInternshipAddress() {
            return {
                street: this.internship.street,
                number: this.internship.streetnumber,
                zipcode: this.internship.zipcode,
                location: this.internship.location,
            };
        },
    },
};
</script>

<style scoped lang="scss">
#internship-modal___BV_modal_outer_ ::v-deep {
    .modal-dialog {
        .modal-content {
            .modal-header {
                position: relative;
                padding: 0;
                height: 175px;
                border-top-right-radius: $border-radius-content;
                border-top-left-radius: $border-radius-content;

                .btn-close {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    font-size: 1.5625rem;

                    &:before {
                        margin-right: 0;
                        font-weight: $font-weight-normal;
                    }
                }
            }
        }
    }
}

.header,
.internship-details-map-location {
    width: 100%;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }
}

.header {
    height: 100%;
}

.internship-details-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    column-gap: $grid-gutter-width;
}

.additional-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');

    .internship-tags ::v-deep {
        width: 100%;

        > div {
            width: inherit;
        }

        label {
            margin-bottom: map-get($grid-gutter-spacing, 'eight');
            min-width: 140px;
        }

        .apprentice-multiselect {
            min-width: 250px;
            max-width: 60%;
        }
    }

    > div {
        display: flex;
        flex-wrap: wrap;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        column-gap: map-get($grid-gutter-spacing, 'twenty-one');
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        width: 100%;

        .label {
            margin: 0 0 map-get($grid-gutter-spacing, 'eight') 0;
            min-width: 140px;
            align-self: flex-start;
        }
    }

    .gg-map-icon {
        color: $primary;
    }
}

.internship-details-description {
    line-height: 1.3;
    margin-top: map-get($grid-gutter-spacing, 'ten');
    margin-bottom: $grid-gutter-width;
}

.internship-details-map-location {
    height: 200px;
}

.motivation {
    margin-top: map-get($grid-gutter-spacing, 'sixty');
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .additional-info {
        row-gap: map-get($grid-gutter-spacing, 'eight');

        > div {
            flex-direction: row;
            margin-bottom: 0;
            align-items: center;

            .label {
                margin: 0;
                align-self: center;
            }
        }

        .internship-tags ::v-deep {
            > div {
                width: calc(100% - 161px);
            }

            label {
                margin-bottom: 0;
            }

            .apprentice-multiselect {
                min-width: 100%;
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .internship-details-header {
        flex-direction: row;

        .multiselect__tags {
            flex: 1 0 250px;
        }
    }

    .additional-info {
        .internship-tags ::v-deep {
            > div {
                width: calc(70% - 161px);
            }
        }
    }
}
</style>
